import { defineStore } from 'pinia'
import { useNetworkStore } from '@/stores/network/store'
import router from '@/router'
import { isTesting, getEKPUrl, environment } from '@/utils/environment'
import { cancelCurrentTour } from '@/tutorials'
import * as Api from './api'
import { config } from '@/core/config'

import {
  createMeta,
  getMetaError,
  getMetaInitial,
  getMetaPending,
  getMetaSuccess,
  getNewRequestId,
  type Meta,
} from '@/utils/store'
import {
  getEkpLoginWithRedirectUrl,
  hasReachedMaxRedirects,
} from '@/utils/urls'
import { isKeycloakUsed, loginKeycloak, logoutKeycloak } from '@/core/keycloak'
import type { WhoamiResponse } from '@/types/backend/whoamiResponse'
import { useNavigationStore } from '../navigation/store'
import { useCoreStore } from '../core/store'
import { storage } from '@/utils/storage/storage'

type State = {
  isLoggedIn: boolean
  refreshTimeout?: NodeJS.Timeout
  accessToken: string
  meta: Meta<{ login: undefined }>
  whoami?: WhoamiResponse
}

const getDefaultState = (): State => ({
  isLoggedIn: false,
  refreshTimeout: undefined,
  accessToken: '',
  meta: createMeta(['login']),
  whoami: undefined,
})

export type OfficeDeskPermission =
  | 'faxPermission'
  | 'smsPermission'
  | 'contactsPermission'
  | 'callLogPermission'

export const useAuthStore = defineStore('auth', {
  state: getDefaultState,

  getters: {
    ctaId: (state) => state.whoami?.pbxId,
    guard: (state) => state.whoami?.guard,
    role: (state) => state.whoami?.role,
    username: (state) => state.whoami?.username,
    email: (state) => state.whoami?.email,
    customer: (state) => state.whoami?.customer,
    officeDeskId: (state) =>
      state.whoami && ['officeDesk', 'officedesk'].includes(state.whoami.role)
        ? state.whoami.id
        : undefined,
    displayName: (state) => state.whoami?.name,
    hasOfficeDeskPermission: (state): OfficeDeskPermission[] => [
      ...(state.whoami?.faxPermission ? ['faxPermission' as const] : []),
      ...(state.whoami?.smsPermission ? ['smsPermission' as const] : []),
      ...(state.whoami?.contactsPermission
        ? ['contactsPermission' as const]
        : []),
      ...(state.whoami?.callLogPermission
        ? ['callLogPermission' as const]
        : []),
    ],
    isOfficeDesk(): boolean {
      return this.officeDeskId !== undefined
    },
    getHasOfficeDeskPermission(): (
      permissionTo: OfficeDeskPermission
    ) => boolean {
      return (parmissionTo: OfficeDeskPermission) =>
        this.hasOfficeDeskPermission.includes(parmissionTo)
    },
    getHasOfficeDeskAccessToExtension: (state) => (extensionId?: number) =>
      state.whoami?.extensions.includes(extensionId!),
    getHasOfficeDeskAccessToDevice: (state) => (deviceId?: number) =>
      state.whoami?.devices.includes(deviceId!),
  },

  actions: {
    async loadUserData() {
      const result = await Api.loadWhoami()

      if (result.status === 'error') {
        if (result.type === 'maintenance') {
          return 'maintenance'
        }

        if (result.type === 'not-found') {
          useNetworkStore().HANDLE_CTA_NOT_FOUND_ERROR(result.payload)
        } else {
          useNetworkStore().HANDLE_LOADING_ERROR(result.payload)
        }

        return 'error'
      }

      this.whoami = result.payload

      return 'success'
    },

    async login({
      value,
      password,
      mode = 'token',
    }: {
      value: string
      password?: string
      mode?: 'token' | 'customerId'
    }) {
      const requestId = getNewRequestId()

      this.meta.login = getMetaPending(requestId, undefined)

      // Get auth token from the customerId if necessary
      const authToken =
        mode === 'customerId'
          ? await this.getTokenWithCustomerId({ value, password })
          : value

      // Ensure the token is valid
      if (
        !authToken ||
        typeof authToken !== 'string' ||
        authToken === 'undefined' ||
        authToken === 'null'
      ) {
        this.logout()

        this.meta.login = getMetaError(
          undefined,
          new Error('auth token undefined')
        )

        return 'error'
      }

      // Store the token for use in api requests
      storage.easybellDeAuth = authToken

      // Load the user data

      const result = await this.loadUserData()

      if (result === 'maintenance') {
        this.meta.login = getMetaError(
          undefined,
          new Error('could not load user data during maintenance')
        )

        return 'maintenance'
      }

      if (result === 'error') {
        this.logout()

        this.meta.login = getMetaError(
          undefined,
          new Error('could not load user data')
        )

        return 'error'
      }

      // Finalize login

      storage.whoami = JSON.stringify(this.whoami)

      storage.authMode = this.guard

      this.isLoggedIn = true

      this.meta.login = getMetaSuccess(undefined)

      this.startTokenRefreshTimer()

      useNavigationStore().load({ useCache: false })

      return 'success'
    },

    async getTokenWithCustomerId({
      value,
      password,
    }: {
      value: string
      password?: string
    }) {
      const result = await Api.loadToken(value, password)

      if (result.status === 'error') {
        return
      }

      const authToken = result.payload.access_token

      return authToken
    },

    logout() {
      this.whoami = undefined

      this.isLoggedIn = false

      storage.whoami = undefined

      storage.easybellDeAuth = undefined

      storage.authMode = undefined

      cancelCurrentTour()

      useCoreStore().resetAll()

      this.meta.login = getMetaInitial()
    },

    goToLogin() {
      const keycloakUsed = isKeycloakUsed()

      this.logout()

      if (isTesting()) {
        router.push('/login')
      } else if (!hasReachedMaxRedirects() && !keycloakUsed) {
        window.location.href = getEkpLoginWithRedirectUrl()
      } else {
        this.goToLogout()
      }
    },

    goToLogout() {
      const keycloakUsed = isKeycloakUsed()

      this.logout()

      if (isTesting()) {
        router.push('/login')
      } else if (keycloakUsed) {
        logoutKeycloak()
      } else {
        window.location.href = `${getEKPUrl()}/logout`
      }
    },

    goToEkp() {
      this.logout()

      window.location.href = `${getEKPUrl()}/`
    },

    goToMyEasybell() {
      this.logout()

      window.location.href = `${config.EKP_URL_COM[environment()]}/`
    },

    async checkLocalStorageForLogin() {
      const authMode = storage.authMode

      const authToken =
        authMode === 'keycloak' ? await loginKeycloak() : storage.easybellDeAuth

      if (authToken !== null && authToken !== undefined) {
        await this.login({ value: authToken })
      }
    },

    async startTokenRefreshTimer() {
      const interval = config.AUTHENTICATON.refreshTokenIntervalSeconds * 1000

      clearTimeout(this.refreshTimeout)

      const refreshTimeout = setTimeout(async () => {
        // Check that is still logged in
        if (!this.isLoggedIn) {
          return
        }

        // Refresh the token

        const result = await Api.loadRefreshToken()

        if (result.status === 'error') {
          useNetworkStore().HANDLE_LOADING_ERROR(result.payload)
        } else {
          storage.easybellDeAuth = result.payload
        }

        this.startTokenRefreshTimer()
      }, interval)

      this.refreshTimeout = refreshTimeout
    },
  },
})
